/**
 * DnD orchestration for the generic Kanban organism.
 *
 * Owns drag session state and handlers only. Does not mutate items —
 * consumers decide what a successful drop means via onCardMove.
 */

import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type DragCancelEvent,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { KanbanDragEntityType } from './Kanban.types';
import type {
  KanbanCardMoveEvent,
  KanbanColumnConfig,
  KanbanDropPreview,
  KanbanFilterDef,
  KanbanGroupOption,
  KanbanItemBase,
  KanbanSortOption,
} from './Kanban.config.types';
import {
  countItemsInColumn,
  findLaneItemsForGroup,
  fromColumnSortId,
  getActiveDragProbeY,
  getKanbanDragType,
  isWipExceeded,
  normalizeColumnOrder,
  resolveCardDropPreview,
} from './kanban.utils';
import { useKanbanDropSettle } from './useKanbanDropSettle';
import { useKanbanLaneLookup } from './useKanbanLaneLookup';

interface UseKanbanDndOptions<TItem extends KanbanItemBase> {
  features: {
    cardDrag: boolean;
    cardReorder: boolean;
    wipLimits: boolean;
    wipBehavior: 'warn' | 'block';
    swimlanes: boolean;
    filters: boolean;
    sorting: boolean;
  };
  items: TItem[];
  itemsById: Map<string, TItem>;
  visibleItems: TItem[];
  groups: Array<{ groupId: string; items: TItem[] }>;
  columns: KanbanColumnConfig[];
  columnsById: Map<string, KanbanColumnConfig>;
  columnOrder: string[];
  enableColumnDrag: boolean;
  swimlanesEnabled: boolean;
  groupBy: string | null;
  groupOptions: KanbanGroupOption<TItem>[];
  columnFilters: Record<string, Record<string, string | null>>;
  columnSortBy: Record<string, string | null>;
  filterDefs: KanbanFilterDef<TItem>[];
  sortOptions: KanbanSortOption<TItem>[];
  onCardMove?: (event: KanbanCardMoveEvent) => void;
  onColumnOrderChange?: (order: string[]) => void;
  onWipViolation?: (columnId: string, limit: number, count: number) => void;
  onDropPreviewChange?: (preview: KanbanDropPreview | null) => void;
  /** Called right before a state change that moves cards (FLIP capture). */
  onLayoutWillChange?: () => void;
  /** Card drag ended; `from` is the held card's last viewport rect. */
  onCardLanding?: (
    itemId: string,
    from: { left: number; top: number } | null
  ) => void;
}

export function useKanbanDnd<TItem extends KanbanItemBase>({
  features,
  items,
  itemsById,
  visibleItems,
  groups,
  columns,
  columnsById,
  columnOrder,
  enableColumnDrag,
  swimlanesEnabled,
  groupBy,
  groupOptions,
  columnFilters,
  columnSortBy,
  filterDefs,
  sortOptions,
  onCardMove,
  onColumnOrderChange,
  onWipViolation,
  onDropPreviewChange,
  onLayoutWillChange,
  onCardLanding,
}: UseKanbanDndOptions<TItem>) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const { settling, startSettle } = useKanbanDropSettle(
    items,
    onLayoutWillChange
  );
  const [activeDragType, setActiveDragType] =
    useState<KanbanDragEntityType | null>(null);
  /** Survives clearDrag so the overlay's drop animation matches the drag. */
  const [lastDragType, setLastDragType] =
    useState<KanbanDragEntityType | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);
  const [dropPreview, setDropPreview] = useState<KanbanDropPreview | null>(
    null
  );
  const dropPreviewRef = useRef<KanbanDropPreview | null>(null);

  const clearDrag = useCallback(() => {
    onLayoutWillChange?.();
    setActiveId(null);
    setActiveDragType(null);
    setOverColumnId(null);
    dropPreviewRef.current = null;
    setDropPreview(null);
    onDropPreviewChange?.(null);
  }, [onDropPreviewChange, onLayoutWillChange]);

  const publishDropPreview = useCallback(
    (next: KanbanDropPreview | null, { animate = true } = {}) => {
      const previous = dropPreviewRef.current;
      const isSameSlot =
        previous?.droppableId === next?.droppableId &&
        previous?.index === next?.index;
      // Drag move fires every frame; re-rendering the board for an unchanged
      // preview is what drops frames.
      if (isSameSlot && previous?.overItemId === next?.overItemId) return;
      if (animate && !isSameSlot) onLayoutWillChange?.();
      dropPreviewRef.current = next;
      setDropPreview(next);
      onDropPreviewChange?.(next);
    },
    [onDropPreviewChange, onLayoutWillChange]
  );

  useEffect(() => {
    if (activeDragType !== 'column' && activeDragType !== 'card') return;
    const previous = document.body.style.cursor;
    document.body.style.cursor = 'grabbing';
    return () => {
      document.body.style.cursor = previous;
    };
  }, [activeDragType]);

  const persistColumnOrder = useCallback(
    (next: string[]) => {
      onColumnOrderChange?.(normalizeColumnOrder(columns, next));
    },
    [columns, onColumnOrderChange]
  );

  const {
    makeDroppableId,
    resolveDropTarget,
    getLaneColumnItems,
    getGroupOption,
    getOwnSlotPreview,
  } = useKanbanLaneLookup({
    features,
    itemsById,
    visibleItems,
    groups,
    columnsById,
    swimlanesEnabled,
    groupBy,
    groupOptions,
    columnFilters,
    columnSortBy,
    filterDefs,
    sortOptions,
  });

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const dragType = getKanbanDragType(event);
      const activate = () => {
        setActiveId(String(event.active.id));
        setActiveDragType(dragType);
        setLastDragType(dragType);
      };
      if (dragType !== 'card') {
        activate();
        return;
      }
      // The held card renders from dnd-kit state, so the board's pickup
      // re-render can run at lower priority.
      startTransition(() => {
        activate();
        const item = itemsById.get(String(event.active.id));
        // Placeholder replaces the source in place: nothing moves, nothing fades.
        if (item) {
          publishDropPreview(getOwnSlotPreview(item), { animate: false });
        }
      });
    },
    [itemsById, publishDropPreview, getOwnSlotPreview]
  );

  const updateCardDropPreview = useCallback(
    (event: DragOverEvent | DragMoveEvent) => {
      if (getKanbanDragType(event) !== 'card' || !features.cardDrag) {
        setOverColumnId(null);
        publishDropPreview(null);
        return;
      }
      // Keep last insertion while the pointer briefly leaves all droppables.
      if (!event.over) return;

      const target = resolveDropTarget(event.over.id);
      if (!target) return;

      const droppableId = makeDroppableId(
        target.groupId ?? '__all__',
        target.columnId
      );
      setOverColumnId(droppableId);

      const activeItem = itemsById.get(String(event.active.id));
      if (!activeItem) {
        publishDropPreview(null);
        return;
      }

      const fromGroupId = getGroupOption()?.getGroupId(activeItem);
      const sameColumn = target.columnId === activeItem.columnId;
      const sameGroup =
        !target.groupId || !fromGroupId || target.groupId === fromGroupId;

      if (sameColumn && sameGroup && !features.cardReorder) {
        publishDropPreview(getOwnSlotPreview(activeItem));
        return;
      }

      const laneItems = findLaneItemsForGroup(
        groups,
        target.groupId,
        visibleItems
      );
      const columnItems = getLaneColumnItems(laneItems, target.columnId);
      const destinationItemIds = columnItems
        .filter((item) => item.id !== activeItem.id)
        .map((item) => item.id);

      if (String(event.over.id) === activeItem.id) {
        const fromIndex = columnItems.findIndex(
          (item) => item.id === activeItem.id
        );
        publishDropPreview({
          droppableId,
          columnId: target.columnId,
          groupId: target.groupId,
          index: Math.max(0, fromIndex),
          overItemId: activeItem.id,
        });
        return;
      }

      const overType = event.over.data.current?.type as string | undefined;
      publishDropPreview(
        resolveCardDropPreview({
          overId: String(event.over.id),
          overType,
          overRect: event.over.rect,
          probeY: getActiveDragProbeY(event.active),
          target,
          droppableId,
          destinationItemIds,
          previous: dropPreviewRef.current,
        })
      );
    },
    [
      features.cardDrag,
      features.cardReorder,
      getGroupOption,
      getOwnSlotPreview,
      resolveDropTarget,
      makeDroppableId,
      itemsById,
      groups,
      visibleItems,
      getLaneColumnItems,
      publishDropPreview,
    ]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      updateCardDropPreview(event);
    },
    [updateCardDropPreview]
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (getKanbanDragType(event) !== 'card') return;
      updateCardDropPreview(event);
    },
    [updateCardDropPreview]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const dragType = getKanbanDragType(event) ?? activeDragType;
      const { active, over } = event;
      const previewAtDrop = dropPreviewRef.current;
      if (dragType === 'card') {
        onCardLanding?.(String(active.id), active.rect.current.translated);
      }
      clearDrag();
      if (!over) return;

      if (dragType === 'column' && enableColumnDrag) {
        const activeColumnId =
          fromColumnSortId(String(active.id)) ?? String(active.id);
        const overTarget = resolveDropTarget(over.id);
        const overColumnResolved = overTarget?.columnId ?? null;
        if (!overColumnResolved) return;
        const oldIndex = columnOrder.indexOf(activeColumnId);
        const newIndex = columnOrder.indexOf(overColumnResolved);
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
        persistColumnOrder(arrayMove(columnOrder, oldIndex, newIndex));
        return;
      }

      if (dragType !== 'card' || !features.cardDrag) return;

      const item = itemsById.get(String(active.id));
      if (!item) return;

      const target = resolveDropTarget(over.id);
      if (!target) return;
      const { columnId: toColumnId, groupId: toGroupId } = target;

      const fromGroupId = getGroupOption()?.getGroupId(item);

      const laneItems = findLaneItemsForGroup(
        groups,
        toGroupId,
        visibleItems
      );
      const columnItems = getLaneColumnItems(laneItems, toColumnId);
      const destinationItemIds = columnItems
        .filter((entry) => entry.id !== item.id)
        .map((entry) => entry.id);

      const previewMatches =
        previewAtDrop != null &&
        previewAtDrop.columnId === toColumnId &&
        (previewAtDrop.groupId ?? '__all__') === (toGroupId ?? '__all__');

      let toIndex: number;
      if (previewMatches) {
        toIndex = previewAtDrop.index;
      } else if (String(over.id) === item.id) {
        toIndex = Math.max(
          0,
          columnItems.findIndex((entry) => entry.id === item.id)
        );
      } else {
        toIndex = resolveCardDropPreview({
          overId: String(over.id),
          overType: over.data.current?.type as string | undefined,
          overRect: over.rect,
          probeY: getActiveDragProbeY(active),
          target,
          droppableId: makeDroppableId(toGroupId ?? '__all__', toColumnId),
          destinationItemIds,
          previous: previewAtDrop,
        }).index;
      }

      const commitMove = (move: KanbanCardMoveEvent) => {
        if (!onCardMove) return;
        onCardMove(move);
        startSettle({
          itemId: item.id,
          preview: {
            droppableId: makeDroppableId(toGroupId ?? '__all__', toColumnId),
            columnId: toColumnId,
            groupId: toGroupId,
            index: toIndex,
            overItemId: null,
          },
        });
      };

      if (toColumnId === item.columnId) {
        if (toGroupId && fromGroupId && toGroupId !== fromGroupId) {
          commitMove({
            itemId: item.id,
            fromColumnId: item.columnId,
            toColumnId,
            toIndex,
            toGroupId,
          });
          return;
        }

        if (!features.cardReorder) return;
        const fromIndex = columnItems.findIndex((entry) => entry.id === item.id);
        if (fromIndex < 0 || toIndex === fromIndex) return;
        commitMove({
          itemId: item.id,
          fromColumnId: item.columnId,
          toColumnId,
          toIndex,
          toGroupId: toGroupId ?? fromGroupId,
        });
        return;
      }

      const targetColumn = columnsById.get(toColumnId);
      if (features.wipLimits && targetColumn?.wipLimit != null) {
        const after = countItemsInColumn(items, toColumnId) + 1;
        if (isWipExceeded(targetColumn, after)) {
          onWipViolation?.(toColumnId, targetColumn.wipLimit, after);
          if (features.wipBehavior === 'block') return;
        }
      }

      commitMove({
        itemId: item.id,
        fromColumnId: item.columnId,
        toColumnId,
        toIndex,
        toGroupId: toGroupId ?? fromGroupId,
      });
    },
    [
      activeDragType,
      clearDrag,
      onCardLanding,
      startSettle,
      getGroupOption,
      enableColumnDrag,
      features.cardDrag,
      features.cardReorder,
      features.wipLimits,
      features.wipBehavior,
      columnOrder,
      persistColumnOrder,
      itemsById,
      resolveDropTarget,
      visibleItems,
      groups,
      getLaneColumnItems,
      makeDroppableId,
      onCardMove,
      columnsById,
      items,
      onWipViolation,
    ]
  );

  const handleDragCancel = useCallback(
    (event: DragCancelEvent) => {
      if (getKanbanDragType(event) === 'card') {
        onCardLanding?.(
          String(event.active.id),
          event.active.rect.current.translated
        );
      }
      clearDrag();
    },
    [clearDrag, onCardLanding]
  );

  const activeItem =
    activeDragType === 'card' && activeId
      ? itemsById.get(activeId) ?? null
      : null;
  const activeColumnId =
    activeDragType === 'column' && activeId
      ? fromColumnSortId(activeId) ?? activeId
      : null;
  const activeColumn = activeColumnId
    ? columnsById.get(activeColumnId) ?? null
    : null;

  return {
    activeDragType,
    lastDragType,
    overColumnId,
    dropPreview,
    settling,
    activeItem,
    activeColumnId,
    activeColumn,
    getLaneColumnItems,
    makeDroppableId,
    handleDragStart,
    handleDragOver,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  };
}
